'use client'

import React, { useMemo } from 'react'
import { BLOCKS } from '@contentful/rich-text-types'
import { richTextToStyledReactComponents } from '@utils/contentful/richTextParser'
import { ProductDeliveryInfo } from './productDetailVariants/ProductDeliveryInfo'
import { useProductPageState } from './useProductPageState'
import { ContentfulMarkdown } from '@utils/contentful/ContentfulMarkdown'
import { ServiceDetailsSection } from './ServiceDetailsSection'
import { usePathname } from 'next/navigation'
import {
  breadcrumbsLinksToStructuredData,
  getPageBreadcrumbs,
} from '@utils/breadcrumbs'
import {
  Breadcrumbs,
  BreadcrumbsItem,
} from '@noissue-ui-kit/oldBranding/breadcrumbs'
import { IBreadcrumbLink } from 'types/breadcrumbs'
import { BreadcrumbJsonLd, ProductJsonLd } from 'next-seo'
import { useProductPageVariation } from './useProductPageVariation'
import { THubspotFormSpecification } from 'app/(auth)/sign-up/profile/_types/hubspotForm'
import { NEXT_PUBLIC_DOMAIN_NAME } from '@utils/client-side-config'
import { documentToPlainTextString } from '@contentful/rich-text-plain-text-renderer'
import { NEXT_PUBLIC_BIGCOMMERCE_CHANNEL_CURRENCY } from '@utils/client-side-config'
import { IProductEntry } from 'app/_services/CMS/contentful/types/productEntry'
import { ProductVariant } from 'app/(main)/(catalog)/upload/[slug]/_components/designUpload/types'
import { ProductTag } from 'app/(main)/(catalog)/category/[slug]/_components/ProductTag'
import { BadgeAlertMessage } from '@noissue-ui-kit/badgeAlertMessage'

export function ProductDetailsSectionClient({
  hubspotPlusForm,
  productVariantsSales,
  productData,
}: {
  hubspotPlusForm: THubspotFormSpecification
  productVariantsSales: ProductVariant[]
  productData: IProductEntry
}) {
  const { pageState, pageVariant } = useProductPageState(
    productVariantsSales,
    productData
  )

  const { priceSection: priceOrAvailabilitySection, mainSection } =
    useProductPageVariation({
      pageState,
      pageVariant,
      hubspotPlusForm,
      productVariantsSales,
      productData,
    })

  const pathname = usePathname()
  const {
    productName,
    productDescription,
    productDeliveryTypes,
    productTags,
    productDeliveryDisclaimer,
    productDetailsBadge,
    serviceDetails,
    breadcrumbsConfig,
    productHeroImages,
    productSeo,
  } = productData

  const productOptionLowestPrice = Math.min(
    ...productVariantsSales.map((variant) => variant.price)
  )

  const breadcrumbLinks: IBreadcrumbLink[] = useMemo(
    () =>
      getPageBreadcrumbs([
        ...breadcrumbsConfig,
        {
          name: productName,
          path: pathname,
        },
      ]),
    [breadcrumbsConfig]
  )

  return (
    <div className="bg-white px-[20px] pb-[40px] pt-[32px] lg:pb-[24px] lg:px-[32px] lg:overflow-auto">
      {breadcrumbLinks?.length > 0 && (
        <Breadcrumbs className="w-full mr-auto mb-4">
          {breadcrumbLinks.map((breadcrumb, index) => (
            <BreadcrumbsItem
              key={`breadcrumbsItem-${index}`}
              href={breadcrumb?.url}
              text={breadcrumb.name}
            />
          ))}
        </Breadcrumbs>
      )}

      <BreadcrumbJsonLd
        itemListElements={breadcrumbsLinksToStructuredData(breadcrumbLinks)}
        useAppDir={true}
      />

      <ProductJsonLd
        productName={productName}
        images={
          productHeroImages?.map(
            (productImage) => `https:${productImage?.fields?.file.url}`
          ) || []
        }
        description={
          productSeo?.seoDescription ||
          documentToPlainTextString(productDescription)
        }
        brand="noissue Custom"
        manufacturerName="noissue"
        manufacturerLogo={`https://${NEXT_PUBLIC_DOMAIN_NAME}/images/logo.svg`}
        aggregateOffer={{
          priceCurrency: NEXT_PUBLIC_BIGCOMMERCE_CHANNEL_CURRENCY,
          lowPrice: productOptionLowestPrice?.toString(),
        }}
        useAppDir={true}
      />

      <h1 className="text-5xl text-[28px] leading-[36px] font-bold text-acai font-mori mb-2">
        {productName}
      </h1>

      {priceOrAvailabilitySection && (
        <div className="mt-1">{priceOrAvailabilitySection}</div>
      )}

      {productDetailsBadge?.title && (
        <BadgeAlertMessage
          size="xs"
          borderType="square-round"
          variant="info"
          className="!bg-bud-20 mt-6 mb-9"
          ctaUrl={productDetailsBadge?.ctaUrl}
        >
          <span className="text-black font-semibold">
            {productDetailsBadge?.title}
          </span>
        </BadgeAlertMessage>
      )}

      {productDescription && (
        <div className="">
          {richTextToStyledReactComponents(productDescription, {
            [BLOCKS.PARAGRAPH]: (_, children) => (
              <p className="pb-3 text-xl font-normal md:text-xl text-core-grey-darkest leading-[20px] font-mori mt-4">
                {children}
              </p>
            ),
          })}
        </div>
      )}

      {pageState !== 'unavailable' && productTags?.length > 0 && (
        <div className="flex w-full flex-wrap my-9">
          {productTags?.map((tag) => (
            <ProductTag
              key={tag.label}
              label={tag.label}
              labelImage={tag.labelImage}
              type={tag.type}
              className="mr-3 mb-3"
            />
          ))}
        </div>
      )}

      {productDeliveryTypes?.length > 0 && (
        <>
          <h5 className="mb-5 text-xl font-mori text-acai">
            Estimated arrival:
          </h5>

          {productDeliveryTypes?.map((deliveryType) => (
            <ProductDeliveryInfo
              key={deliveryType.label}
              label={deliveryType.label}
              minDays={deliveryType?.minDays}
              maxDays={deliveryType?.maxDays}
              deliveryTypeIcon={deliveryType?.deliveryTypeIcon}
              helpText={deliveryType?.helpText}
              className="!bg-paper mb-3"
            />
          ))}
        </>
      )}

      {productDeliveryDisclaimer && (
        <ContentfulMarkdown
          overrideClass="text-lg font-mori text-black-80 mt-5"
          overrideStyles={{
            a: ({ children, href }) => (
              <a
                className="text-lg font-mori text-black-80 mt-5 font-semibold"
                href={href}
                target="_blank"
                rel="noopener noreferrer"
              >
                {` ${children} `}
              </a>
            ),
          }}
        >
          {productDeliveryDisclaimer}
        </ContentfulMarkdown>
      )}

      <hr className="w-full bg-pebble h-1 my-9" />
      {mainSection && <div className="mb-10 mt-7">{mainSection}</div>}

      {Boolean(serviceDetails?.length) && (
        <hr className="w-full bg-pebble h-1 my-9" />
      )}

      <ServiceDetailsSection serviceDetails={serviceDetails} />
    </div>
  )
}
